import { EventEmitter, Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { ToolsWebService } from './tools.web.service';
import { Room, Building, Campus, PatchManagerRequestType, PatchManagerResultDto, FindHostResultDto, PrtgConnectResultDto, CopyProtocolsResultDto } from 'UvExplorerServer.Dtos.TS/Tools';
import { IGetPortStateResultDto, ISetPortStateResultDto, PortStateDto, PortStateType } from 'UvExplorerServer.Dtos.TS/Session';
import { MessageService } from 'base';
import { IdentityService } from 'identity';
import { NavigationService } from 'netview';


export class PatchManagerData {

	public campuses: Campus[];
	public get hasCampuses(): boolean {
		return !!this.campuses;
	}

	public buildings: Building[];
	public get hasBuildings(): boolean {
		return !!this.buildings;
	}
}


@Injectable()
export class ToolsService {

	public devicesChanged: EventEmitter<string[]>;

	public patchManagerData: PatchManagerData;
	private loadingPatchManagerData: boolean;
	public patchManagerDataLoaded: EventEmitter<PatchManagerData>;

	constructor(private toolsWebService: ToolsWebService,
		private messageService: MessageService,
		private identityService: IdentityService,
		private navService: NavigationService) {

		this.devicesChanged = new EventEmitter<string[]>();

		this.patchManagerData = null;
		this.loadingPatchManagerData = false;
		this.patchManagerDataLoaded = new EventEmitter<PatchManagerData>();
	}

	public get hasPatchManagerData(): boolean {
		return !!this.patchManagerData;
	}

	public notifyDevicesChanged(changedDeviceGuids: string[]): void {
		this.devicesChanged.emit(changedDeviceGuids);
	}

	public loadPatchManagerData(): void {

		if (this.loadingPatchManagerData || this.hasPatchManagerData) {
			return;
		}
		else {
			this.loadingPatchManagerData = true;
		}

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.messageService.startWork();
		this.getCampuses(sessionGuid, netViewGuid, agentGuid, schedDiscGuid, schedDiscRsltGuid).subscribe(
			(result: Campus[]) => {
				let allCampuses = result;

				this.getBuildings(sessionGuid, netViewGuid, agentGuid, schedDiscGuid, schedDiscRsltGuid).subscribe(
					(result2: Building[]) => {
						this.messageService.endWork();
						let allBuildings = result2;

						this.patchManagerData = new PatchManagerData();
						this.patchManagerData.campuses = allCampuses;
						this.patchManagerData.buildings = allBuildings;
						
						this.loadingPatchManagerData = false;
						
						this.patchManagerDataLoaded.emit(this.patchManagerData);
					},
					(error2) => {
						this.messageService.endWork();
						this.messageService.displayErrorMessage("Building download failed");
						this.loadingPatchManagerData = false;	
					});
			},
			(error) => {
				this.messageService.endWork();
				this.messageService.displayErrorMessage("Campus download failed");
				this.loadingPatchManagerData = false;	
			});
	}

	private getCampuses(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string): Observable<Campus[]> {

		let subject = new Subject<Campus[]>();

		this.toolsWebService.processPatchManagerRequest(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, PatchManagerRequestType.GetCampuses, "", "", "", "", "", false).subscribe(
				(result: PatchManagerResultDto) => {
					if (result.succeeded && !!result.campuses) {
						subject.next(result.campuses);
					}
					else {
						subject.error(result.message);
					}
				},
				(error) => {
					subject.error("getCampuses failed");
				}
			)

		return subject.asObservable();
	}

	private getBuildings(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string): Observable<Building[]> {

		let subject = new Subject<Building[]>();

		this.toolsWebService.processPatchManagerRequest(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, PatchManagerRequestType.GetBuildings, "", "", "", "", "", false).subscribe(
				(result: PatchManagerResultDto) => {
					if (result.succeeded && !!result.buildings) {
						subject.next(result.buildings);
					}
					else {
						subject.error(result.message);
					}
				},
				(error) => {
					subject.error("getBuildings failed");
				}
			)

		return subject.asObservable();
	}

	public getBuildingRooms(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, campusName: string, buildingName: string): Observable<Room[]> {

		let subject = new Subject<Room[]>();

		this.toolsWebService.processPatchManagerRequest(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, PatchManagerRequestType.GetRooms, campusName, buildingName, "", "", "", false).subscribe(
				(result: PatchManagerResultDto) => {
					if (result.succeeded && !!result.rooms) {
						subject.next(result.rooms);
					}
					else {
						subject.error(result.message);
					}
				},
				(error) => {
					subject.error("getBuildingRooms failed");
				}
			)

		return subject.asObservable();
	}

	public copyNetworkProtocols(sessionGuid: string, sourceNetwork: string, targetNetwork: string, copyAll: boolean): Observable<CopyProtocolsResultDto> {

		let subject = new Subject<CopyProtocolsResultDto>();

		this.toolsWebService.processCopyNetworkProtocols(sessionGuid, sourceNetwork, targetNetwork, copyAll).subscribe(
			(result: CopyProtocolsResultDto) => {
					if (result.succeeded) {
						subject.next(result);
					}
					else {
						subject.error(result.message);
					}
				},
				(error) => {
					subject.error("copyNetworkProtocols failed");
				}
			)

		return subject.asObservable();
	}


	public lookupJackToSwitchPort(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, campusName: string, buildingName: string,
		jackName: string, doLive: boolean): Observable<PatchManagerResultDto> {

		let subject = new Subject<PatchManagerResultDto>();

		this.toolsWebService.processPatchManagerRequest(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, PatchManagerRequestType.LookupJackToSwitchPort, campusName, buildingName,
			jackName, "", "", doLive).subscribe(
				(result: PatchManagerResultDto) => {
					if (result.succeeded) {
						subject.next(result);
					}
					else {
						subject.error(result.message);
					}
				},
				(error) => {
					subject.error("lookupJackToSwitchPort failed");
				}
			)

		return subject.asObservable();
	}

	public findHost(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, dnsHostname: string, hostIpAddress: string, hostMacAddress: string, doLive: boolean): Observable<FindHostResultDto>
	{
		let subject = new Subject<FindHostResultDto>();

		this.messageService.startWork();

		this.toolsWebService.processFindHostRequest(sessionGuid, netViewGuid, agentGuid, schedDiscGuid, schedDiscRsltGuid, dnsHostname, hostIpAddress, hostMacAddress, doLive).subscribe(
			(result: FindHostResultDto) => {
				this.messageService.endWork();
				if (result.succeeded) {
						subject.next(result);
				}
				else {
					subject.error(result.message);
				}
			},
			(error) => {
					this.messageService.endWork();
					subject.error("findHost failed");
				}
			)

		return subject.asObservable();
	}


	public lookupSwitchToJack(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, deviceGuid: string, doLive: boolean): Observable<PatchManagerResultDto> {

		let subject = new Subject<PatchManagerResultDto>();

		this.toolsWebService.processPatchManagerRequest(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, PatchManagerRequestType.LookupSwitchToJack, "", "", "", deviceGuid, "", doLive).subscribe(
				(result: PatchManagerResultDto) => {
					if (result.succeeded) {
						subject.next(result);
					}
					else {
						subject.error(result.message);
					}
				},
				(error) => {
					subject.error("lookupSwitchToJack failed");
				}
			)

		return subject.asObservable();
	}

	public lookupRoomToJacks(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, campusName: string, buildingName: string, roomName: string, doLive: boolean): Observable<PatchManagerResultDto> {

		let subject = new Subject<PatchManagerResultDto>();

		this.toolsWebService.processPatchManagerRequest(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, PatchManagerRequestType.LookupRoomToJacks, campusName, buildingName, "", "", roomName, doLive).subscribe(
				(result: PatchManagerResultDto) => {
					if (result.succeeded) {
						subject.next(result);
					}
					else {
						subject.error(result.message);
					}
				},
				(error) => {
					subject.error("lookupRoomToJacks failed");
				}
			)

		return subject.asObservable();
	}


	public getPortState(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, stateTypes: PortStateType[], deviceGuids:
			string[], ifIndexes: number[], showBusy: boolean): Observable<IGetPortStateResultDto> {

		let subject = new Subject<IGetPortStateResultDto>();

		if (showBusy) {
			this.messageService.startWork();
		}
		this.toolsWebService.getPortState(sessionGuid, netViewGuid, agentGuid,
			schedDiscGuid, schedDiscRsltGuid, stateTypes, deviceGuids, ifIndexes).subscribe(
				(result) => {
					if (showBusy) {
						this.messageService.endWork();
					}
					subject.next(result);
				},
				(error) => {
					if (showBusy) {
						this.messageService.endWork();
					}
					console.log(error);
					subject.error(error);
				});

		return subject.asObservable();
	}

	public setPortState(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, stateType: PortStateType, entries: PortStateDto[], showBusy: boolean)
		: Observable<ISetPortStateResultDto> {

		let subject = new Subject<ISetPortStateResultDto>();

		if (showBusy) {
			this.messageService.startWork();
		}
		this.toolsWebService.setPortState(sessionGuid, netViewGuid, agentGuid,
			schedDiscGuid, schedDiscRsltGuid, stateType, entries).subscribe(
				(result) => {
					if (showBusy) {
						this.messageService.endWork();
					}
					subject.next(result);
					this.notifyDevicesChanged(this.getDeviceGuids(entries));
				},
				(error) => {
					if (showBusy) {
						this.messageService.endWork();
					}
					console.log(error);
					subject.error(error);
					this.notifyDevicesChanged(this.getDeviceGuids(entries));
				});

		return subject.asObservable();
	}

	private getDeviceGuids(entries: PortStateDto[]): string[] {
		let deviceGuids = new Set<string>();
		entries.forEach((entry: PortStateDto) => deviceGuids.add(entry.DeviceGuid));
		return Array.from(deviceGuids);
	}

	public testPrtgConnection(prtgServerURL: string, prtgUsername: string, prtgPasshash: string, prtgApiKey: string): Observable<PrtgConnectResultDto> {

		let subject = new Subject<PrtgConnectResultDto>();

		this.messageService.startWork();

		this.toolsWebService.testPrtgConnect(prtgServerURL, prtgUsername, prtgPasshash, prtgApiKey).subscribe(
			(result: PrtgConnectResultDto) => {
				this.messageService.endWork();
				if (result.succeeded) {
					subject.next(result);
				}
				else {
					subject.error(result.message);
				}
			},
			(error) => {
				this.messageService.endWork();
				subject.error("PRTG Connection failed");
			}
		)

		return subject.asObservable();
	}
}
